import React, { useState } from "react"
import { AuthenticatedLayout } from "@/components/layout/authenticated-layout"
import { Head, router } from "@inertiajs/react"
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { 
    Plus, 
    Edit, 
    Trash2, 
    Shield, 
    ArrowLeft, 
    Users, 
    Save,
    RefreshCw
} from "lucide-react"
import { useToast } from "@/hooks/use-toast"

export default function DepartmentPermissions({ 
    department, 
    departmentRoles, 
    availableRoles, 
    availablePermissions 
}) {
    const [isCreateOpen, setIsCreateOpen] = useState(false)
    const [isEditOpen, setIsEditOpen] = useState(false)
    const [isDeleteOpen, setIsDeleteOpen] = useState(false)
    const [editingRole, setEditingRole] = useState(null)
    const [deletingRole, setDeletingRole] = useState(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [formData, setFormData] = useState({
        role_id: "",
        permissions: [],
    })
    const { toast } = useToast()

    const handleCreate = () => {
        setFormData({ role_id: "", permissions: [] })
        setIsCreateOpen(true)
    }

    const handleEdit = (departmentRole) => {
        setEditingRole(departmentRole)
        setFormData({
            role_id: departmentRole.role_id,
            permissions: departmentRole.permissions || [],
        })
        setIsEditOpen(true)
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        setIsSubmitting(true)

        const url = editingRole 
            ? `/departments/${department.id}/roles/${editingRole.id}`
            : `/departments/${department.id}/roles`
        
        const method = editingRole ? 'put' : 'post'

        router[method](url, formData, {
            onSuccess: () => {
                setIsCreateOpen(false)
                setIsEditOpen(false)
                setEditingRole(null)
                setFormData({ role_id: "", permissions: [] })
                setIsSubmitting(false)
                toast({
                    title: "Success",
                    description: `Department role ${editingRole ? 'updated' : 'created'} successfully.`,
                })
            },
            onError: (errors) => {
                setIsSubmitting(false)
                toast({
                    title: "Error",
                    description: `Failed to ${editingRole ? 'update' : 'create'} department role.`,
                    variant: "destructive",
                })
            },
        })
    }

    const handleDelete = () => {
        setIsSubmitting(true)
        
        router.delete(`/departments/${department.id}/roles/${deletingRole.id}`, {
            onSuccess: () => {
                setIsDeleteOpen(false)
                setDeletingRole(null)
                setIsSubmitting(false)
                toast({
                    title: "Success",
                    description: "Department role removed successfully.",
                })
            },
            onError: (errors) => {
                setIsSubmitting(false)
                toast({
                    title: "Error",
                    description: "Failed to remove department role.",
                    variant: "destructive",
                })
            },
        })
    }

    const handleSyncDefaults = () => {
        setIsSubmitting(true)
        
        router.post(`/departments/${department.id}/sync-default-permissions`, {}, {
            onSuccess: () => {
                setIsSubmitting(false)
                toast({
                    title: "Success",
                    description: "Default permissions synced for all users in this department.",
                })
            },
            onError: (errors) => {
                setIsSubmitting(false)
                toast({
                    title: "Error",
                    description: "Failed to sync default permissions.",
                    variant: "destructive",
                })
            },
        })
    }

    const handlePermissionToggle = (permissionName, checked) => {
        setFormData(prev => ({
            ...prev,
            permissions: checked 
                ? [...prev.permissions, permissionName]
                : prev.permissions.filter(p => p !== permissionName)
        }))
    }

    const handleCategoryToggle = (categoryPermissions, checked) => {
        setFormData(prev => ({
            ...prev,
            permissions: checked
                ? [...new Set([...prev.permissions, ...categoryPermissions])]
                : prev.permissions.filter(p => !categoryPermissions.includes(p))
        }))
    }

    const getAvailableRoles = () => {
        const usedRoleIds = departmentRoles.map(dr => dr.role_id)
        return availableRoles.filter(role => !usedRoleIds.includes(role.id))
    }

    const formatPermissionName = (name) => {
        return name.split('_').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ')
    }

    const formatCategoryName = (category) => {
        return category.charAt(0).toUpperCase() + category.slice(1)
    }

    return (
        <AuthenticatedLayout>
            <Head title={`DME | ${department.name} Permissions`} />
            
            <div className="flex flex-col gap-6 pb-8 pt-6 px-4 md:px-6 w-full">
                <div className="flex items-center gap-4">
                    <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => router.visit('/departments')}
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back to Departments
                    </Button>
                </div>

                <div className="flex flex-col gap-2">
                    <h1 className="text-3xl font-bold tracking-tight">
                        {department.name} Permissions
                    </h1>
                    <p className="text-muted-foreground">
                        Manage role-based permissions for the {department.name} department
                    </p>
                </div>

                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3">
                        <Shield className="h-5 w-5 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                            {departmentRoles.length} role{departmentRoles.length !== 1 ? 's' : ''} configured
                        </span>
                    </div>
                    
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleSyncDefaults}
                            disabled={isSubmitting || departmentRoles.length === 0}
                        >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Sync to Users
                        </Button>
                        <Button onClick={handleCreate} disabled={getAvailableRoles().length === 0}>
                            <Plus className="h-4 w-4 mr-2" />
                            Add Role
                        </Button>
                    </div>
                </div>

                <Card>
                    <CardContent className="p-0">
                        {departmentRoles.length === 0 ? (
                            <div className="text-center py-12">
                                <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                                <h3 className="text-lg font-medium mb-2">No roles configured</h3>
                                <p className="text-muted-foreground mb-4">
                                    Set up role-based permissions for this department.
                                </p>
                                <Button onClick={handleCreate} disabled={getAvailableRoles().length === 0}>
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add First Role
                                </Button>
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow className="hover:bg-transparent">
                                        <TableHead>Role</TableHead>
                                        <TableHead>Permissions</TableHead>
                                        <TableHead>Users</TableHead>
                                        <TableHead className="w-12"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {departmentRoles.map((departmentRole) => (
                                        <TableRow key={departmentRole.id}>
                                            <TableCell>
                                                <div className="flex items-center space-x-2">
                                                    <Badge variant="outline">
                                                        {departmentRole.role}
                                                    </Badge>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex flex-wrap gap-1 max-w-md">
                                                    {departmentRole.permissions.slice(0, 3).map((permission) => (
                                                        <Badge 
                                                            key={permission} 
                                                            variant="secondary" 
                                                            className="text-xs"
                                                        >
                                                            {formatPermissionName(permission)}
                                                        </Badge>
                                                    ))}
                                                    {departmentRole.permissions.length > 3 && (
                                                        <Badge variant="secondary" className="text-xs">
                                                            +{departmentRole.permissions.length - 3} more
                                                        </Badge>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center text-sm text-muted-foreground">
                                                    <Users className="h-4 w-4 mr-1" />
                                                    {/* This would need to be calculated in the backend */}
                                                    -
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleEdit(departmentRole)}
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => {
                                                            setDeletingRole(departmentRole)
                                                            setIsDeleteOpen(true)
                                                        }}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>

                {/* Create/Edit Dialog */}
                <Dialog open={isCreateOpen || isEditOpen} onOpenChange={() => {
                    setIsCreateOpen(false)
                    setIsEditOpen(false)
                    setEditingRole(null)
                }}>
                    <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>
                                {editingRole ? 'Edit' : 'Add'} Department Role
                            </DialogTitle>
                            <DialogDescription>
                                Configure permissions for a role in the {department.name} department.
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleSubmit}>
                            <div className="space-y-6 py-4">
                                {!editingRole && (
                                    <div className="space-y-2">
                                        <Label htmlFor="role">Role</Label>
                                        <Select
                                            value={formData.role_id}
                                            onValueChange={(value) => setFormData({...formData, role_id: value})}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select a role" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {getAvailableRoles().map((role) => (
                                                    <SelectItem key={role.id} value={role.id.toString()}>
                                                        {role.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}

                                <div className="space-y-4">
                                    <Label>Permissions</Label>
                                    {Object.entries(availablePermissions).map(([category, permissions]) => {
                                        const categoryPermissions = permissions.map(p => p.name)
                                        const allSelected = categoryPermissions.every(p => formData.permissions.includes(p))
                                        const someSelected = categoryPermissions.some(p => formData.permissions.includes(p))
                                        
                                        return (
                                            <Card key={category} className="p-4">
                                                <div className="space-y-3">
                                                    <div className="flex items-center space-x-2">
                                                        <Checkbox
                                                            id={`category-${category}`}
                                                            checked={allSelected}
                                                            ref={ref => {
                                                                if (ref) ref.indeterminate = someSelected && !allSelected
                                                            }}
                                                            onCheckedChange={(checked) => 
                                                                handleCategoryToggle(categoryPermissions, checked)
                                                            }
                                                        />
                                                        <Label 
                                                            htmlFor={`category-${category}`}
                                                            className="text-sm font-medium"
                                                        >
                                                            {formatCategoryName(category)}
                                                        </Label>
                                                    </div>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 ml-6">
                                                        {permissions.map((permission) => (
                                                            <div key={permission.name} className="flex items-center space-x-2">
                                                                <Checkbox
                                                                    id={permission.name}
                                                                    checked={formData.permissions.includes(permission.name)}
                                                                    onCheckedChange={(checked) => 
                                                                        handlePermissionToggle(permission.name, checked)
                                                                    }
                                                                />
                                                                <Label 
                                                                    htmlFor={permission.name}
                                                                    className="text-sm"
                                                                >
                                                                    {formatPermissionName(permission.name)}
                                                                </Label>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </Card>
                                        )
                                    })}
                                </div>
                            </div>
                            <DialogFooter>
                                <Button 
                                    type="button" 
                                    variant="outline" 
                                    onClick={() => {
                                        setIsCreateOpen(false)
                                        setIsEditOpen(false)
                                        setEditingRole(null)
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button type="submit" disabled={isSubmitting}>
                                    <Save className="h-4 w-4 mr-2" />
                                    {editingRole ? 'Update' : 'Create'} Role
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Remove Department Role</AlertDialogTitle>
                            <AlertDialogDescription>
                                Are you sure you want to remove the "{deletingRole?.role}" role 
                                from the {department.name} department? This will affect users 
                                with this role in this department.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={handleDelete}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                                Remove Role
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>
        </AuthenticatedLayout>
    )
} 